import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  Zap,
  Newspaper,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  Image as ImageIcon,
  Copy,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [aggregating, setAggregating] = useState(false);
  const [lastRunResult, setLastRunResult] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      toast.error('Admin-Zugang erforderlich');
      navigate('/');
      return;
    }
    fetchStatus();
  }, [user, authLoading, navigate]);

  const fetchStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await axios.get(`${API}/admin/aggregation-status`);
      setStatus(response.data);
    } catch (err) {
      toast.error('Status konnte nicht geladen werden');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleAggregate = async () => {
    setAggregating(true);
    setLastRunResult(null);
    toast.info('Aggregation gestartet... Dies dauert 1-2 Minuten.');
    try {
      const response = await axios.post(`${API}/admin/aggregate`);
      setLastRunResult(response.data);
      toast.success(
        `Aggregation abgeschlossen: ${response.data.stored} neue Artikel gespeichert`
      );
      fetchStatus();
    } catch (err) {
      toast.error('Aggregation fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAggregating(false);
    }
  };

  if (authLoading || statusLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF] dark:text-[#3377FF]">
              Admin
            </span>
            <span className="h-1 w-1 rounded-full bg-gray-400" />
            <span className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{user.email}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#09090B] dark:text-[#FAFAFA]">
            Dashboard
          </h1>
          <p className="text-[#52525B] dark:text-[#A1A1AA] mt-2">
            Verwaltung von News-Aggregation und Systemstatus
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          <StatCard
            icon={<Newspaper className="h-5 w-5" />}
            label="Nachrichten gesamt"
            value={status?.total_news ?? '—'}
            color="bg-blue-500/10 text-blue-600 dark:text-blue-400"
            testid="stat-total-news"
          />
          <StatCard
            icon={<Zap className="h-5 w-5" />}
            label="Breaking News"
            value={status?.breaking_count ?? '—'}
            color="bg-red-500/10 text-red-600 dark:text-red-400"
            testid="stat-breaking-count"
          />
          <StatCard
            icon={<TrendingUp className="h-5 w-5" />}
            label="Scheduler"
            value={
              status?.scheduler_running ? (
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Aktiv
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  Gestoppt
                </span>
              )
            }
            color="bg-green-500/10 text-green-600 dark:text-green-400"
            testid="stat-scheduler-status"
          />
        </div>

        {/* Action Panel */}
        <div className="bg-white dark:bg-[#121212] rounded-2xl border border-black/10 dark:border-white/10 p-6 sm:p-8 mb-10">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-[#09090B] dark:text-[#FAFAFA]">
                News-Aggregation
              </h2>
              <p className="text-sm text-[#52525B] dark:text-[#A1A1AA] mt-1">
                Läuft automatisch stündlich. Manuell auslösen für sofortiges Update.
              </p>
            </div>
            <Button
              onClick={handleAggregate}
              disabled={aggregating}
              className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="trigger-aggregation-button"
            >
              {aggregating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Läuft...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Aggregation starten
                </>
              )}
            </Button>
          </div>

          {/* Last Run Details */}
          <div className="border-t border-black/10 dark:border-white/10 pt-6">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] dark:text-[#A1A1AA] mb-4">
              Letzter Durchlauf
            </h3>

            {lastRunResult ? (
              <RunResultCard result={lastRunResult} justRan />
            ) : status?.last_run ? (
              <RunResultCard result={status.last_run} />
            ) : (
              <p className="text-sm text-[#52525B] dark:text-[#A1A1AA]">
                Noch kein Durchlauf gespeichert (Scheduler läuft stündlich).
              </p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-[#121212] rounded-2xl border border-black/10 dark:border-white/10 p-6 sm:p-8">
          <h2 className="text-2xl font-bold tracking-tight text-[#09090B] dark:text-[#FAFAFA] mb-4">
            Weitere Aktionen
          </h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="rounded-full"
              data-testid="view-live-site-button"
            >
              <Newspaper className="h-4 w-4 mr-2" />
              Live-Seite ansehen
            </Button>
            <Button
              variant="outline"
              onClick={fetchStatus}
              className="rounded-full"
              data-testid="refresh-status-button"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Status neu laden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white dark:bg-[#121212] rounded-2xl border border-black/10 dark:border-white/10 p-6"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] dark:text-[#A1A1AA]">
          {label}
        </span>
        <div className={`p-2 rounded-full ${color}`}>{icon}</div>
      </div>
      <div className="text-3xl font-extrabold tracking-tight text-[#09090B] dark:text-[#FAFAFA]">
        {value}
      </div>
    </motion.div>
  );
}

function RunResultCard({ result, justRan = false }) {
  const timestamp = result.timestamp;
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        <span className="text-sm font-medium text-[#09090B] dark:text-[#FAFAFA]">
          {justRan ? 'Gerade eben ausgeführt' : 'Letzter Lauf'}
        </span>
        {timestamp && (
          <span className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
            · {formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: de })}
          </span>
        )}
        {result.trigger && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-[#52525B] dark:text-[#A1A1AA]">
            {result.trigger}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Metric label="Gefetcht" value={result.fetched ?? 0} />
        <Metric label="Nach Dedup" value={result.new_after_dedupe ?? 0} />
        <Metric label="Hash-Dupli" value={result.hash_filtered ?? 0} muted />
        <Metric label="Semantisch-Dupli" value={result.semantic_filtered ?? 0} muted />
        <Metric label="OG-Bilder" value={result.og_images_recovered ?? 0} muted icon={<ImageIcon className="h-3 w-3" />} />
        <Metric label="Gespeichert" value={result.stored ?? 0} highlight />
      </div>
      {result.notifications_sent > 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm">
          <Zap className="h-4 w-4 text-yellow-500" />
          <span className="text-[#52525B] dark:text-[#A1A1AA]">
            {result.notifications_sent} Push-Benachrichtigungen versendet
          </span>
        </div>
      )}
      {result.errors && result.errors.length > 0 && (
        <div className="mt-4 flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Fehler:</div>
            <ul className="list-disc list-inside">
              {result.errors.map((e) => (
                <li key={`err-${e}`}>{e}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, muted = false, highlight = false, icon }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-[#52525B] dark:text-[#A1A1AA] mb-1 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div
        className={`text-2xl font-bold ${
          highlight
            ? 'text-[#0055FF] dark:text-[#3377FF]'
            : muted
            ? 'text-[#52525B] dark:text-[#A1A1AA]'
            : 'text-[#09090B] dark:text-[#FAFAFA]'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export default AdminDashboard;
